import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/searchable-select';
import {
  useCreatePolicy,
  useFlintProducers,
  useInsuranceCompanies,
  useInsuranceCompanyCommissionRates,
  usePolicyTypes,
} from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatDateInput, formatDollar, unformatDollar } from '@/lib/formatters';
import { FieldError } from '@/components/FieldError';
import { POLICY_STATUS_OPTIONS, POLICY_SUBSTATUS_OPTIONS } from './policy-status';
import { computeTotalPremium } from '@shared/premium';
import { isValidPolicyDate } from '@shared/policy-date';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  customerName: string;
  onCreated?: (policyId: string) => void;
}

// CSR-1124 — DoD spec: Payment Plan dropdown with exactly two options.
const PAYMENT_PLAN_OPTIONS = [
  { value: 'Paid in Full', label: 'Paid in Full' },
  { value: 'Monthly Payments', label: 'Monthly Payments' },
] as const;

const STATUS_NONE = '__none__';
const SUBSTATUS_NONE = '__none__';
const PAYMENT_NONE = '__none__';

export function AddPolicyDialog({ open, onOpenChange, insuredId, customerName, onCreated }: Props) {
  const [policyNumber, setPolicyNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [type, setType] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [status, setStatus] = useState('');
  const [substatus, setSubstatus] = useState('');
  // CSR-1604 — premium breakdown; raw digits. Total is derived.
  const [policyPremium, setPolicyPremium] = useState('');
  const [brokerFee, setBrokerFee] = useState('');
  const [policyFees, setPolicyFees] = useState('');
  const [taxes, setTaxes] = useState('');
  // CSR-1606 — Commission (%) defaults from the carrier's commission config
  // (line-of-business override, else general rate). `commissionEdited` guards
  // the pre-fill: once the agent types a value, defaults never overwrite it.
  const [commissionPct, setCommissionPct] = useState('');
  const [commissionEdited, setCommissionEdited] = useState(false);
  const [producer, setProducer] = useState('');
  const [agency, setAgency] = useState('');
  const [mga, setMga] = useState('');
  const [financeCompany, setFinanceCompany] = useState('');
  const [paymentPlan, setPaymentPlan] = useState('');
  const [boundDate, setBoundDate] = useState('');

  const create = useCreatePolicy();
  const { toast } = useToast();
  const { data: companies } = useInsuranceCompanies();
  const { data: producers } = useFlintProducers();
  const { data: policyTypes } = usePolicyTypes();

  // CSR-1606 — resolve the selected carrier name to its company row so the
  // commission defaults (general + per-line overrides) can be looked up. A
  // custom-typed carrier has no row and therefore no defaults.
  const selectedCarrier = useMemo(
    () =>
      (companies?.insuranceCarriers ?? []).find(
        (c) => c.name.trim().toLowerCase() === carrier.trim().toLowerCase(),
      ) ?? null,
    [companies, carrier],
  );
  const { data: commissionRates } = useInsuranceCompanyCommissionRates(
    selectedCarrier?.id,
  );

  useEffect(() => {
    if (commissionEdited) return;
    if (!selectedCarrier) {
      // Codex review — a custom-typed / unmatched carrier has no defaults;
      // clear the pre-fill so the previous carrier's rate can't be submitted
      // silently.
      setCommissionPct('');
      return;
    }
    const override = (commissionRates ?? []).find(
      (rate) =>
        rate.lineOfBusiness.trim().toLowerCase() === type.trim().toLowerCase(),
    );
    const defaultRate = override?.ratePct ?? selectedCarrier.newCommissionPct ?? '';
    setCommissionPct(defaultRate === null ? '' : String(defaultRate));
  }, [commissionEdited, selectedCarrier, commissionRates, type]);

  const carrierOptions = useMemo(
    () =>
      (companies?.insuranceCarriers ?? []).map((c) => ({
        value: c.name,
        label: c.name,
      })),
    [companies],
  );
  const mgaOptions = useMemo(
    () => (companies?.mgas ?? []).map((m) => ({ value: m.name, label: m.name })),
    [companies],
  );
  const producerOptions = useMemo(
    () => (producers ?? []).map((p) => ({ value: p.fullName, label: p.fullName })),
    [producers],
  );

  const effectiveDateError =
    effectiveDate.trim() !== '' && !isValidPolicyDate(effectiveDate);
  const expirationDateError =
    expirationDate.trim() !== '' && !isValidPolicyDate(expirationDate);
  const boundDateError =
    boundDate.trim() !== '' && !isValidPolicyDate(boundDate);

  const canSubmit =
    !create.isPending &&
    policyNumber.trim() !== '' &&
    carrier.trim() !== '' &&
    isValidPolicyDate(effectiveDate) &&
    !expirationDateError &&
    !boundDateError;

  // CSR-1604 — Total recalculates live as the CSR types. Empty breakdown → "".
  const totalPremium = useMemo(
    () => computeTotalPremium({ policyPremium, brokerFee, policyFees, taxes }),
    [policyPremium, brokerFee, policyFees, taxes],
  );

  const handleSubmit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        insuredId,
        customerName,
        policyNumber: policyNumber.trim(),
        carrier: carrier.trim(),
        effectiveDate: effectiveDate.trim(),
        expirationDate: expirationDate.trim() || null,
        status: status.trim() || null,
        // CSR-2336 — only sent when chosen, so an unset substatus adds no key.
        ...(substatus ? { substatus } : {}),
        // CSR-1604 — persist the breakdown; `premium` is the derived Total
        // (server re-derives it authoritatively, see routes.ts). Components are
        // null when blank; never submit "0" from an empty dollar mask.
        type: type.trim() || null,
        policyPremium: policyPremium.trim() || null,
        brokerFee: brokerFee.trim() || null,
        policyFees: policyFees.trim() || null,
        taxes: taxes.trim() || null,
        commissionPct: commissionPct.trim() || null,
        premium: totalPremium || null,
        producer: producer.trim() || null,
        agency: agency.trim() || null,
        mga: mga.trim() || null,
        financeCompany: financeCompany.trim() || null,
        paymentPlan: paymentPlan.trim() || null,
        boundDate: boundDate.trim() || null,
      } as any,
      {
        onSuccess: (created) => {
          toast({ title: 'Policy created' });
          onOpenChange(false);
          const id = (created as { id?: string })?.id;
          if (onCreated && id) {
            onCreated(id);
          }
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to create policy';
          const isDcFailure = message.includes('DC write failed');
          toast({
            title: isDcFailure ? 'Data Center rejected the policy' : 'Failed to create policy',
            description: message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add policy</DialogTitle>
          <DialogDescription>
            Create a new policy for {customerName}. Most fields sync from the carrier — manual
            edits may be overwritten on the next sync.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="add-policy-number">Policy number *</Label>
            <Input
              id="add-policy-number"
              data-testid="add-policy-number"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-carrier">Carrier *</Label>
            <SearchableSelect
              value={carrier}
              onChange={setCarrier}
              options={carrierOptions}
              allowCustom
              placeholder="Select or type a carrier…"
            />
            {/* Test-only handle: drives the same state as the SearchableSelect
                so component tests can `fireEvent.change` without driving
                Radix's popover (hard in happy-dom). type=text + sr-only keeps
                React's onChange wired (hidden inputs don't fire change). */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="add-policy-carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-type">Type</Label>
            <SearchableSelect
              id="add-policy-type"
              value={type}
              onChange={setType}
              options={[
                { value: '', label: '—' },
                ...(policyTypes ?? []).map((t) => ({ value: t, label: t })),
              ]}
              placeholder="Select line of business…"
            />
            {/* Test-only handle — same pattern as the carrier field above. */}
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="add-policy-type-input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-effective-date">Effective date *</Label>
            <Input
              id="add-policy-effective-date"
              data-testid="add-policy-effective-date"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(formatDateInput(e.target.value))}
              className={effectiveDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={effectiveDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
          <div>
            <Label htmlFor="add-policy-expiration-date">Expiration date</Label>
            <Input
              id="add-policy-expiration-date"
              data-testid="add-policy-expiration-date"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={expirationDate}
              onChange={(e) => setExpirationDate(formatDateInput(e.target.value))}
              className={expirationDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={expirationDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
          <div>
            <Label htmlFor="add-policy-status">Status</Label>
            <Select
              value={status || STATUS_NONE}
              onValueChange={(v) => setStatus(v === STATUS_NONE ? '' : v)}
            >
              <SelectTrigger id="add-policy-status" data-testid="add-policy-status">
                <SelectValue placeholder="Select status…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STATUS_NONE}>—</SelectItem>
                {POLICY_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="add-policy-substatus">Substatus</Label>
            <Select
              value={substatus || SUBSTATUS_NONE}
              onValueChange={(v) => setSubstatus(v === SUBSTATUS_NONE ? '' : v)}
            >
              <SelectTrigger id="add-policy-substatus" data-testid="add-policy-substatus">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SUBSTATUS_NONE}>None</SelectItem>
                {POLICY_SUBSTATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="add-policy-premium">Policy Premium</Label>
              <Input
                id="add-policy-premium"
                data-testid="add-policy-premium"
                inputMode="decimal"
                placeholder="$0"
                value={policyPremium ? formatDollar(policyPremium) : ''}
                onChange={(e) => setPolicyPremium(unformatDollar(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="add-policy-broker-fee">Broker Fee</Label>
              <Input
                id="add-policy-broker-fee"
                data-testid="add-policy-broker-fee"
                inputMode="decimal"
                placeholder="$0"
                value={brokerFee ? formatDollar(brokerFee) : ''}
                onChange={(e) => setBrokerFee(unformatDollar(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="add-policy-fees">Finance Charge</Label>
              <Input
                id="add-policy-fees"
                data-testid="add-policy-fees"
                inputMode="decimal"
                placeholder="$0"
                value={policyFees ? formatDollar(policyFees) : ''}
                onChange={(e) => setPolicyFees(unformatDollar(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="add-policy-taxes">Taxes</Label>
              <Input
                id="add-policy-taxes"
                data-testid="add-policy-taxes"
                inputMode="decimal"
                placeholder="$0"
                value={taxes ? formatDollar(taxes) : ''}
                onChange={(e) => setTaxes(unformatDollar(e.target.value))}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="add-policy-commission">Commission (%)</Label>
            <Input
              id="add-policy-commission"
              data-testid="add-policy-commission"
              inputMode="decimal"
              placeholder="0"
              value={commissionPct}
              onChange={(e) => {
                setCommissionPct(e.target.value);
                setCommissionEdited(true);
              }}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-total-premium">Total Premium</Label>
            <Input
              id="add-policy-total-premium"
              data-testid="add-policy-total-premium"
              readOnly
              tabIndex={-1}
              className="bg-muted"
              value={totalPremium ? formatDollar(totalPremium.replace(/\.00$/, '')) : '$0'}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-producer">Producer</Label>
            <SearchableSelect
              value={producer}
              onChange={setProducer}
              options={producerOptions}
              allowCustom
              placeholder="Select or type a producer…"
            />
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="add-policy-producer"
              value={producer}
              onChange={(e) => setProducer(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-agency">Agency</Label>
            <Input
              id="add-policy-agency"
              data-testid="add-policy-agency"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-mga">MGA</Label>
            <SearchableSelect
              value={mga}
              onChange={setMga}
              options={mgaOptions}
              allowCustom
              placeholder="Select or type an MGA…"
            />
            <input
              type="text"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid="add-policy-mga"
              value={mga}
              onChange={(e) => setMga(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-finance">Finance company</Label>
            <Input
              id="add-policy-finance"
              data-testid="add-policy-finance"
              value={financeCompany}
              onChange={(e) => setFinanceCompany(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="add-policy-payment-plan">Payment plan</Label>
            <Select
              value={paymentPlan || PAYMENT_NONE}
              onValueChange={(v) => setPaymentPlan(v === PAYMENT_NONE ? '' : v)}
            >
              <SelectTrigger id="add-policy-payment-plan" data-testid="add-policy-payment-plan">
                <SelectValue placeholder="Select payment plan…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PAYMENT_NONE}>—</SelectItem>
                {PAYMENT_PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="add-policy-bound">Bound date</Label>
            <Input
              id="add-policy-bound"
              data-testid="add-policy-bound"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              value={boundDate}
              onChange={(e) => setBoundDate(formatDateInput(e.target.value))}
              className={boundDateError ? 'border-destructive' : undefined}
            />
            <FieldError show={boundDateError} message="Enter a valid date as MM/DD/YYYY" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button
            data-testid="add-policy-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="cursor-pointer"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
